import { ConvexError } from "convex/values";

export const MAX_BODY_LENGTH = 280;

const FIXED = {
  not_paid: "Chat is for Upside members.",
  no_handle: "Pick a username first.",
  removed: "You were removed from chat.",
  duplicate: "That repeats your last message.",
  empty: "Type a message first.",
  too_long: `Keep it to ${MAX_BODY_LENGTH} characters.`,
  link: "Links to other sites are off in chat.",
  tout: "Chat is for prices and reasoning. Lock, guarantee and paid-pick talk is not allowed here.",
  blocked_word: "That word is not allowed in chat.",
  not_staff: "Only staff can do that.",
  not_found: "That message or person is no longer in chat.",
  own_message: "You can't report your own message.",
  handle_format: "Use 3 to 20 letters, numbers or underscores.",
  handle_reserved: "That username is reserved.",
  handle_not_allowed: "That username is not allowed. Pick another.",
  handle_taken: "That username is taken.",
  handle_too_soon: "You can change your username once an hour.",
  share_locked: "You can only share plays from tools on your plan.",
  bad_share: "That play can't be shared.",
  own_play: "That's your own play.",
  tail_mismatch: "That play changed. Open it again to tail it.",
  play_started: "That game already started.",
  bad_emoji: "Pick an emoji.",
  too_many_reactions: "Five reactions per message.",
  bio_too_long: "Keep your bio to 160 characters.",
  avatar_type: "Use a PNG, JPEG, WebP or GIF.",
  avatar_size: "Keep your photo under 8 MB.",
  avatar_gif_plan: "GIF photos come with Diamond.",
  bad_avatar: "That upload didn't finish. Try again.",
  self_follow: "You can't follow yourself.",
  follow_limit: "You can follow up to 100 people.",
  play_gone: "That play is no longer on the board.",
  quote_unavailable: "Live odds are unavailable right now. Try again in a moment.",
  chat_unavailable: "Chat is unavailable right now. Try again in a moment.",
} as const;

const TIMED = {
  muted: (wait: string) => `You are muted. You can post again in ${wait}.`,
  account_too_new: (wait: string) => `New accounts can post after 10 minutes. Try again in ${wait}.`,
  rate_limited: (wait: string) => `Too fast. Try again in ${wait}.`,
} as const;

export type FixedRefusalCode = keyof typeof FIXED;
export type TimedRefusalCode = keyof typeof TIMED;

export type ChatRefusal =
  | { code: FixedRefusalCode; message: string }
  | { code: TimedRefusalCode; message: string; retryAfterMs: number };

export function waitText(ms: number): string {
  if (ms < 60_000) return `${Math.max(1, Math.ceil(ms / 1000))}s`;
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.ceil(minutes / 60);
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
}

export function refusal(code: FixedRefusalCode): ChatRefusal;
export function refusal(code: TimedRefusalCode, retryAfterMs: number): ChatRefusal;
export function refusal(code: FixedRefusalCode | TimedRefusalCode, retryAfterMs = 0): ChatRefusal {
  return isTimed(code) ? { code, message: TIMED[code](waitText(retryAfterMs)), retryAfterMs } : { code, message: FIXED[code] };
}

export function refuse(code: FixedRefusalCode): never;
export function refuse(code: TimedRefusalCode, retryAfterMs: number): never;
export function refuse(code: FixedRefusalCode | TimedRefusalCode, retryAfterMs = 0): never {
  throw new ConvexError<ChatRefusal>(isTimed(code) ? refusal(code, retryAfterMs) : refusal(code));
}

function isTimed(code: FixedRefusalCode | TimedRefusalCode): code is TimedRefusalCode {
  return code in TIMED;
}
